/** iter 42 — Mobile / QR Scan.
 *
 * Uses html5-qrcode (browser camera + decoder). On Capacitor the same WebView
 * camera permission flow applies; for native barcode scanning you can later
 * swap in @capacitor-community/barcode-scanner with the same `onDecoded`
 * callback wiring.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "../../lib/api";
import MobileShell from "../MobileShell";

export default function MScan() {
  const navigate = useNavigate();
  const elId = "m-qr-reader";
  const scannerRef = useRef(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState("");

  // Resolve a decoded payload via the backend and navigate to the right route.
  const onDecoded = async (text) => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/mobile/qr/${encodeURIComponent(text)}/resolve`);
      const { target_type, target_id } = r.data;
      if (target_type === "job") navigate(`/m/jobs/${target_id}`);
      else if (target_type === "route") navigate(`/m/routes/${target_id}`);
      else setErr(`Unsupported QR target type: ${target_type}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const html5 = new Html5Qrcode(elId);
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (cancelled) return;
            html5.pause(true);
            onDecoded(decoded).finally(() => {
              try { html5.resume(); } catch { /* ignore */ }
            });
          },
          () => { /* per-frame decode errors — ignore */ },
        );
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Camera unavailable");
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => { /* ignore */ });
      }
    };
  }, []);

  return (
    <MobileShell title="QR Scan">
      {err && (
        <div className="m-card" style={{ background: "#fee2e2", color: "#991b1b" }} data-testid="m-scan-error">
          {String(err)}
        </div>
      )}
      <div className="m-card">
        <div id={elId} style={{ width: "100%", borderRadius: 8, overflow: "hidden" }} data-testid="m-scan-camera" />
        <div className="m-card-sub" style={{ marginTop: 8 }}>
          Point the camera at a job QR code. The app will open the linked job.
        </div>
      </div>

      <div className="m-card">
        <div className="m-field-label">Manual entry (QR / Job ID)</div>
        <input
          className="m-input"
          type="text"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          placeholder="paste or type ID…"
          data-testid="m-scan-manual-input"
        />
        <button
          type="button"
          className="m-btn m-btn-primary"
          style={{ marginTop: 8 }}
          onClick={() => manual.trim() && onDecoded(manual.trim())}
          disabled={busy || !manual.trim()}
          data-testid="m-scan-manual-go"
        >GO</button>
      </div>
    </MobileShell>
  );
}
